import styles from "./App.module.css";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { trackEvent } from "./analytics";
import { APP_STORE_URL, MarketingFooter, MarketingNav } from "./Components/MarketingSite";

const outcomes = [
  ["Catch up by ear", "Turn an article into audio you can listen to while you walk, cook, or commute."],
  ["Get the point fast", "Paste a link and get the main points, then ask questions about what it says."],
  ["Make something to share", "Turn a few words into art, a photo edit, an MP3, a podcast, or a song."],
  ["Keep what you find", "Send a page from Safari and keep the result in your Library, with a reminder if you want one."],
];

const whyCreata = [
  ["One input, many formats", "A link, a shared page, pasted text, or a screenshot can become a listen, a summary, a Q&A, an MP3, a two-host podcast, a song, or an image, from the same box."],
  ["Reads what you are reading", "Share to Creata from Safari or any app. The Safari button can send the page you are looking at, and Apple News stories can be read from screenshots."],
  ["Free on-device listening", "Listening with your phone's own voice and saving a voice file cost nothing and run on your device. Paid features show their price before you confirm."],
  ["You say yes first", "Before a photo, recording, or text is sent to an AI provider, the app asks and names who gets it. You can change your mind in Profile."],
];

const plans = [
  ["Free", "Free", "150 credits a month (up to 12 a day), plus 2 to claim every day.", ["Chat, summaries, images and Magic Edit with credits", "Listen and on-device voice file", "Library and reminders"]],
  ["Starter", "$4.99 / month", "400 credits a month, plus 8 to claim every day.", ["Everything in Free, with no daily limit", "MP3, two-host podcast, and song with vocals", "Voice Changer"]],
  ["Pro", "$9.99 / month", "1,200 credits a month, plus 12 to claim every day.", ["Everything in Starter", "Three times the monthly credits of Starter", "For people who make something every day"]],
];

const creditCosts = [
  ["Summary or answer", "1"],
  ["Image", "2"],
  ["Magic Edit", "12"],
  ["MP3", "5 per 1,000 characters"],
  ["Podcast", "30"],
  ["Song with vocals", "40"],
  ["Voice Changer", "20"],
];

const chatFeatures = [
  ["Listen", "Hear any article or text read aloud, with a full-screen reader you can tap to jump around."],
  ["Summarize and ask", "Get a quick summary of a link, or ask questions about what you shared."],
  ["Share to Creata", "Send a page from Safari or another app, or read an Apple News story from screenshots."],
  ["Library and reminders", "Everything you make is saved, and you can set a reminder on any result."],
];

const gallery = [
  ["01_say_it_get_it", "Say it, get it: Creata AI's chat home"],
  ["02_paste_a_link", "Paste a link and pick what to make: listen, summary, questions, podcast, MP3"],
  ["03_send_from_safari", "The Creata button in Safari offering to send the page you are reading"],
  ["04_listen", "Full-screen reader that follows the voice"],
  ["05_mp3", "An MP3 made from text, with the price shown before you start"],
  ["06_song", "Song lyrics written for you, then turned into a song"],
  ["07_create_art", "Create art from a few words"],
  ["08_magic_edit", "Magic Edit changing a photo from a short description"],
  ["09_free_credits", "Credit breakdown and a daily free claim"],
];

const audioFeatures = [
  ["MP3, podcast and song", "Turn text into an MP3, a two-host podcast, or a song with vocals on Starter and Pro.", "Available now"],
  ["Music generation", "Turn a text direction into a short musical idea.", "Available now"],
  ["Voice Changer", "Re-record a short clip in another voice on Starter and Pro.", "Available now"],
  ["Text to speech and voice clone", "Give a script a voice, or create with a voice sample you own or have permission to use.", "Available now"],
];

const portalLinks = [
  ["Explore", "Browse visual ideas and creative directions.", "/explore", "Explore ideas"],
  ["Guides", "Get more from Creata AI’s creative tools.", "/aitoolsguide", "Browse guides"],
  ["Support", "Find help when you need it.", "/support", "Get support"],
];

const ecosystemLinks = [
  ["AI Humanity", "Emotion models and voice tools for more human AI.", "https://aihumanity.io"],
  ["FromSource", "One idea, every platform, in your voice.", "https://fromsource.io"],
  ["Tale2Reel", "Turn an idea into a finished short drama.", "https://tale2reel.com"],
];

const Home = () => {
  const theme = createTheme({ palette: { mode: "dark" } });
  const trackAppStoreClick = (placement) => {
    trackEvent("app_store_click", {
      app_name: "Creata AI",
      placement,
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <main className={styles.landingPage}>
        <MarketingNav isHome />

        <section className={styles.hero} aria-labelledby="hero-title">
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}><AutoAwesomeIcon fontSize="small" /> Made for iPhone</p>
            <h1 id="hero-title">Make something personal.<br /><em>Keep it personal.</em></h1>
            <p className={styles.heroLead}>
              Creata AI is a chat-first creative studio for iPhone. Say what you want, paste a link, or share a page, then listen, summarize, create art, edit a photo, or make audio.
            </p>
            <div className={styles.heroActions}>
              <a className={styles.primaryCta} href={APP_STORE_URL} target="_blank" rel="noreferrer" onClick={() => trackAppStoreClick("hero")}>
                Download on the App Store <ArrowForwardIcon fontSize="small" />
              </a>
              <a className={styles.textCta} href="#privacy" onClick={() => trackEvent("privacy_link_click", { placement: "hero" })}>How privacy works</a>
            </div>
            <p className={styles.platformNote}>Available for iPhone · Daily free credits</p>
          </div>
          <div className={styles.heroVisual} aria-label="Creata AI chat home">
            <div className={styles.heroOrb} />
            <div className={styles.phoneFrame}>
              <img src="/img/app-screens/chat-home.png" alt="Creata AI chat home on iPhone" />
            </div>
            <div className={styles.floatingPrompt}>“Paste a link. Listen, summarize, or make it audio.”</div>
          </div>
        </section>

        <section className={styles.trustStrip} aria-label="Creata AI highlights">
          <span>Chat-first home</span><i />
          <span>Listen + summarize</span><i />
          <span>Art + photo editing</span><i />
          <span>Audio + songs</span>
        </section>

        <section className={styles.outcomesSection} aria-labelledby="outcomes-title">
          <div className={styles.sectionIntro}>
            <p className={styles.eyebrow}>Start with a feeling</p>
            <h2 id="outcomes-title">A better first draft for whatever is next.</h2>
            <p>Choose a starting point, then make it your own. Creata AI keeps the process quick enough to follow an idea while it is still alive.</p>
          </div>
          <div className={styles.outcomeGrid}>
            {outcomes.map(([title, copy], index) => (
              <article className={styles.outcomeCard} key={title}>
                <span>0{index + 1}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.outcomesSection} id="why" aria-labelledby="why-title">
          <div className={styles.sectionIntro}>
            <p className={styles.eyebrow}>Why Creata AI</p>
            <h2 id="why-title">One place to read, listen, and make.</h2>
            <p>Most apps do one of these. Creata AI puts them behind one box, so what you find can become whatever you need next.</p>
          </div>
          <div className={styles.outcomeGrid}>
            {whyCreata.map(([title, copy], index) => (
              <article className={styles.outcomeCard} key={title}>
                <span>0{index + 1}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.productSection} id="chat-first" aria-labelledby="chat-title">
          <div className={styles.productVisuals}>
            <figure className={styles.promptScreen}>
              <img src="/img/app-screens/summary.png" alt="Creata AI summary of a shared link with Listen, Copy and Share actions" />
            </figure>
            <figure className={styles.toolboxScreen}>
              <img src="/img/app-screens/listen-reader.png" alt="Creata AI full-screen reader highlighting the paragraph being read aloud" />
            </figure>
          </div>
          <div className={styles.productCopy}>
            <p className={styles.eyebrow}>New in 5.4</p>
            <h2 id="chat-title">Say it, get it.</h2>
            <p>One box for everything. Type, paste a link, or share a page to Creata, and pick what to make from it.</p>
            <ol className={styles.stepsList}>
              {chatFeatures.map(([title, copy]) => (
                <li key={title}><strong>{title}.</strong><span>{copy}</span></li>
              ))}
            </ol>
          </div>
        </section>

        <section className={styles.portalSection} id="see-it" aria-labelledby="see-it-title">
          <div className={styles.sectionIntro}>
            <p className={styles.eyebrow}>See it in action</p>
            <h2 id="see-it-title">From a link to what you need.</h2>
          </div>
          <div className={styles.galleryStrip}>
            {gallery.map(([file, alt]) => (
              <figure className={styles.galleryItem} key={file}>
                <img src={`/img/store/${file}.jpg`} alt={alt} loading="lazy" width="600" height="1303" />
              </figure>
            ))}
          </div>
        </section>

        <section className={styles.productSection} id="how-it-works" aria-labelledby="product-title">
          <div className={styles.productVisuals}>
            <figure className={styles.promptScreen}>
              <img src="/img/app-screens/create-prompt.png" alt="Creata AI prompt controls" />
            </figure>
            <figure className={styles.toolboxScreen}>
              <img src="/img/app-screens/create-toolbox.png" alt="Creata AI creative toolbox" />
            </figure>
          </div>
          <div className={styles.productCopy}>
            <p className={styles.eyebrow}>Create without the detour</p>
            <h2 id="product-title">One place for the spark, the prompt, and the next version.</h2>
            <ol className={styles.stepsList}>
              <li><strong>Start simply.</strong><span>Write a prompt or choose a quick template.</span></li>
              <li><strong>Shape the direction.</strong><span>Use the tools and chat to get closer to what you imagined.</span></li>
              <li><strong>Keep the momentum.</strong><span>Your next idea, tools, and history stay close at hand.</span></li>
            </ol>
          </div>
        </section>

        <section className={styles.audioSection} aria-labelledby="audio-title">
          <div className={styles.audioVisual}>
            <div className={styles.audioGlow} />
            <figure className={styles.audioScreen}>
              <img src="/img/ai_music_appscreen.jpg" alt="Creata AI music generation screen" />
            </figure>
            <p>Sound starts with an idea.</p>
          </div>
          <div className={styles.audioCopy}>
            <p className={styles.eyebrow}>Beyond images</p>
            <h2 id="audio-title">A creative studio that speaks and sings, and soon moves.</h2>
            <p>Creata AI brings sound into the same fast creative flow, so an article, an idea, or a visual can find its voice or soundtrack without leaving your phone.</p>
            <div className={styles.audioFeatureGrid}>
              {audioFeatures.map(([title, copy, status]) => (
                <article className={styles.audioFeature} key={title}>
                  <span className={status === "Available now" ? styles.availableStatus : styles.futureStatus}>{status}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
            <p className={styles.videoRoadmap}><strong>Video is next.</strong> It will be clearly marked as coming soon until it is ready in Creata AI.</p>
            <a className={styles.audioCta} href="/create" onClick={() => trackEvent("audio_section_click", { destination: "create" })}>
              Explore Creata AI audio <ArrowForwardIcon fontSize="small" />
            </a>
          </div>
        </section>

        <section className={styles.portalSection} id="plans" aria-labelledby="plans-title">
          <div className={styles.sectionIntro}>
            <p className={styles.eyebrow}>Plans</p>
            <h2 id="plans-title">Start free. Upgrade when you want more.</h2>
            <p>Credits pay for what you make, and the price shows before you confirm. Subscriptions are billed through the App Store in US dollars and renew monthly until cancelled. Prices in your country may differ.</p>
          </div>
          <div className={styles.planGrid}>
            {plans.map(([name, price, credits, items]) => (
              <article className={styles.planCard} key={name}>
                <h3>{name}</h3>
                <p className={styles.planPrice}>{price}</p>
                <p>{credits}</p>
                <ul>
                  {items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </article>
            ))}
          </div>
          <h3 className={styles.costsTitle}>What things cost, in credits</h3>
          <dl className={styles.costList}>
            {creditCosts.map(([what, cost]) => (
              <div key={what}><dt>{what}</dt><dd>{cost}</dd></div>
            ))}
          </dl>
          <p className={styles.costNote}>Costs are shown in the app before you confirm and can change. The app is the source of truth.</p>
        </section>

        <section className={styles.privacySection} id="privacy" aria-labelledby="privacy-title">
          <div className={styles.privacyMark}>✦</div>
          <div>
            <p className={styles.eyebrow}>Privacy</p>
            <h2 id="privacy-title">You decide what leaves your phone.</h2>
            <p className={styles.privacyLead}>
              Some features run on your device. Others need an AI service in the cloud. The app tells you which is which, and asks before it sends anything.
            </p>
            <div className={styles.privacyPoints}>
              <p><strong>Asked first, by name</strong><span>Before a photo, a recording, or text goes to an AI provider, a prompt names who receives it. You can withdraw permission in Profile.</span></p>
              <p><strong>Stays on your device</strong><span>Listening with your phone's voice, saving a voice file, reading text from screenshots, reminders, and the Safari button's page capture.</span></p>
              <p><strong>Plain details</strong><span>The privacy notice lists each provider, what it receives, and how long audio is kept (7 days).</span></p>
            </div>
            <p className={styles.roadmapNote}>Read the full <a href="/privacy.html">privacy notice</a>. We are working toward doing more on the device over time, and we will say so when it ships.</p>
          </div>
        </section>

        <section className={styles.downloadSection} aria-labelledby="download-title">
          <p className={styles.eyebrow}>Start creating today</p>
          <h2 id="download-title">Your next idea is already on your phone.</h2>
          <p>Download Creata AI for iPhone and start with daily free credits. Paid audio features need a Starter or Pro plan.</p>
          <a className={styles.primaryCta} href={APP_STORE_URL} target="_blank" rel="noreferrer" onClick={() => trackAppStoreClick("closing_cta")}>
            Download on the App Store <ArrowForwardIcon fontSize="small" />
          </a>
        </section>

        <section className={styles.portalSection} aria-labelledby="portal-title">
          <div className={styles.sectionIntro}>
            <p className={styles.eyebrow}>Creata AI portal</p>
            <h2 id="portal-title">More ways to keep moving.</h2>
          </div>
          <div className={styles.portalGrid}>
            {portalLinks.map(([title, copy, href, label]) => (
              <a className={styles.portalCard} href={href} onClick={() => trackEvent("portal_link_click", { destination: title })} key={title}>
                <h3>{title}</h3>
                <p>{copy}</p>
                <span>{label} <ArrowForwardIcon fontSize="inherit" /></span>
              </a>
            ))}
          </div>
        </section>

        <section className={`${styles.portalSection} ${styles.ecosystemSection}`} aria-labelledby="ecosystem-title">
          <div className={styles.sectionIntro}>
            <p className={styles.eyebrow}>The Creata family</p>
            <h2 id="ecosystem-title">More From Creata AI</h2>
            <p>Creata AI started as an art app. It has grown into a family of AI products for making, expressing, and building.</p>
          </div>
          <div className={styles.portalGrid}>
            {ecosystemLinks.map(([title, copy, href]) => (
              <a className={`${styles.portalCard} ${styles.ecosystemCard}`} href={href} target="_blank" rel="noreferrer" onClick={() => trackEvent("ecosystem_link_click", { destination: title })} key={title}>
                <h3>{title}</h3>
                <p>{copy}</p>
                <span>Visit {title} <ArrowForwardIcon fontSize="inherit" /></span>
              </a>
            ))}
          </div>
        </section>

        <MarketingFooter />
      </main>
    </ThemeProvider>
  );
};

export default Home;
